import { useCallback, useEffect, useRef, useState } from 'react'
import { getBlogMaterial, saveBlogDraft } from '../../../api/blog/blogApi'
import { createLocalId, getLocalMaterial, upsertLocalMaterial } from '../../../api/blog/blogLocalStore'
import { createBlogDraftPreparer } from '../../../api/blog/prepareBlogDraft'
import { downloadBlogBackup } from '../../../api/blog/downloadBlogBackup'

const fingerprint = (record) => JSON.stringify({ ...record?.draft, contentSavedAt: 0 })
const recoveryKey = (record) => `${record?.id}:${fingerprint(record)}`
const recoveryError =
  'Не вдалося зберегти локальну копію. Збережіть статтю або завантажте резервну копію перед виходом.'

// Local recovery is automatic; only Save commits an article to the server.
function useBlogAutosave({ materialRecord, enabled = true, onSaved }) {
  const [status, setStatus] = useState({ kind: 'idle', message: '' })
  const [isSaving, setIsSaving] = useState(false)
  const [conflict, setConflict] = useState(null)
  const [persistedKey, setPersistedKey] = useState(null)
  const latestRef = useRef(materialRecord)
  const enabledRef = useRef(enabled)
  const callbackRef = useRef(onSaved)
  const revisionRef = useRef(materialRecord?.draftRevision || 0)
  const savedFingerprintRef = useRef(null)
  const persistedKeyRef = useRef(null)
  const conflictRef = useRef(null)
  const inFlightRef = useRef(null)
  const requestRef = useRef(null)
  const prepareRef = useRef(null)
  const mountedRef = useRef(true)
  const discardedIdRef = useRef(null)
  if (!prepareRef.current) prepareRef.current = createBlogDraftPreparer()
  latestRef.current = materialRecord
  enabledRef.current = enabled && discardedIdRef.current !== materialRecord?.id
  callbackRef.current = onSaved

  const rememberPersisted = useCallback((record) => {
    if (!record?._localPersist?.ok || !mountedRef.current) return
    if (record.id !== latestRef.current?.id || fingerprint(record) !== fingerprint(latestRef.current)) return
    persistedKeyRef.current = recoveryKey(record)
    setPersistedKey(persistedKeyRef.current)
  }, [])

  const persistLocal = useCallback(async () => {
    const current = latestRef.current
    if (!enabledRef.current || !current?.id) return null
    const local = await upsertLocalMaterial({
      ...current,
      draftRevision: revisionRef.current,
      _serverSynced: fingerprint(current) === savedFingerprintRef.current,
    })
    rememberPersisted(local)
    return local
  }, [rememberPersisted])

  useEffect(() => {
    if (!enabled || !materialRecord?.id) return undefined
    const timer = setTimeout(async () => {
      const local = await persistLocal()
      if (!mountedRef.current || !local || inFlightRef.current || conflictRef.current) return
      if (recoveryKey(local) !== recoveryKey(latestRef.current)) return
      if (!local._localPersist?.ok && !local._serverSynced) {
        setStatus({ kind: 'error', message: recoveryError })
      } else {
        setStatus({
          kind: local._serverSynced ? 'saved-server' : 'saved-local',
          message: local._serverSynced ? 'Збережено' : 'Чернетку збережено локально. Натисніть «Зберегти».',
        })
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [materialRecord, enabled, persistLocal])

  useEffect(() => {
    mountedRef.current = true
    const handleUnload = (event) => {
      const current = latestRef.current
      const unsafe =
        enabledRef.current &&
        current?.id &&
        fingerprint(current) !== savedFingerprintRef.current &&
        recoveryKey(current) !== persistedKeyRef.current
      // An IndexedDB transaction cannot be awaited during browser shutdown.
      if (inFlightRef.current || unsafe) {
        event.preventDefault()
        event.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleUnload)
    return () => {
      mountedRef.current = false
      window.removeEventListener('beforeunload', handleUnload)
      void persistLocal()
    }
  }, [persistLocal])

  const markSynced = useCallback((record) => {
    revisionRef.current = record?.draftRevision || 0
    savedFingerprintRef.current = record?._serverSynced ? fingerprint(record) : null
    persistedKeyRef.current = record?._localPersist?.ok === false ? null : recoveryKey(record)
    setPersistedKey(persistedKeyRef.current)
    conflictRef.current = null
    setConflict(null)
    setStatus({
      kind: record?._serverSynced ? 'saved-server' : record?._localPersist?.ok === false ? 'error' : 'saved-local',
      message: record?._serverSynced
        ? 'Збережено'
        : record?._localPersist?.ok === false
        ? recoveryError
        : 'Чернетку збережено локально',
    })
  }, [])

  const inspectServerVersion = useCallback((remote) => {
    if (!remote || remote.id !== latestRef.current?.id || remote.draftRevision === revisionRef.current) return
    const next = { remote, localRevision: revisionRef.current }
    conflictRef.current = next
    setConflict(next)
    setStatus({
      kind: 'conflict',
      message: 'Версії статті відрізняються. Перегляньте серверну версію та виберіть, яку залишити.',
    })
  }, [])

  const saveNow = useCallback(
    (options = {}) => {
      if (inFlightRef.current) return inFlightRef.current
      const current = latestRef.current
      if (!enabledRef.current || !current?.id) return Promise.resolve({ ok: false })
      const expectedRevision = Number.isInteger(options.replaceRevision) ? options.replaceRevision : revisionRef.current
      const requestKey = `${current.id}:${expectedRevision}:${fingerprint(current)}`
      if (requestRef.current?.key !== requestKey) requestRef.current = { key: requestKey, saveId: createLocalId() }
      const saveId = requestRef.current.saveId
      void persistLocal()
      setIsSaving(true)
      setStatus({ kind: 'dirty', message: 'Зберігаємо статтю та медіа…' })
      const task = (async () => {
        try {
          const draft = await prepareRef.current(current)
          const remote = await saveBlogDraft({ materialId: current.id, expectedRevision, saveId, draft })
          if (remote.code === 'aborted') {
            const server = await getBlogMaterial(current.id)
            if (mountedRef.current && latestRef.current?.id === current.id) {
              if (server.ok && server.material) inspectServerVersion(server.material)
              else
                setStatus({
                  kind: 'error',
                  message: 'Версії відрізняються. Не вдалося завантажити серверну копію. Спробуйте зберегти ще раз.',
                })
            }
            return { ...remote, message: 'Версії статті відрізняються. Виберіть потрібну версію нижче.' }
          }
          if (!remote.ok || !remote.material) throw new Error(remote.message || 'Не вдалося зберегти статтю')
          if (discardedIdRef.current === current.id) return remote
          if (!mountedRef.current || latestRef.current?.id !== current.id) {
            const local = await getLocalMaterial(current.id)
            await upsertLocalMaterial(
              local && fingerprint(local) !== fingerprint(current)
                ? { ...local, draftRevision: remote.material.draftRevision, _serverSynced: false }
                : { ...remote.material, _serverSynced: true }
            )
            return remote
          }
          revisionRef.current = remote.material.draftRevision
          const unchanged = fingerprint(latestRef.current) === fingerprint(current)
          savedFingerprintRef.current = fingerprint(remote.material)
          conflictRef.current = null
          setConflict(null)
          const next = unchanged
            ? { ...remote.material, _serverSynced: true }
            : { ...latestRef.current, draftRevision: remote.material.draftRevision, _serverSynced: false }
          latestRef.current = next
          callbackRef.current?.(next, unchanged)
          setStatus({
            kind: unchanged ? 'saved-server' : 'saved-local',
            message: unchanged
              ? `Збережено${remote.softWarn ? '. Стаття наближається до ліміту розміру.' : ''}`
              : 'Попередню версію збережено. Збережіть нові зміни ще раз.',
          })
          rememberPersisted(await upsertLocalMaterial(next))
          return { ...remote, unchanged }
        } catch (error) {
          void persistLocal()
          if (mountedRef.current && latestRef.current?.id === current.id) {
            setStatus({ kind: 'error', message: `Не вдалося зберегти: ${error.message}` })
          }
          return { ok: false, message: error.message }
        } finally {
          inFlightRef.current = null
          if (mountedRef.current) setIsSaving(false)
        }
      })()
      inFlightRef.current = task
      return task
    },
    [persistLocal, inspectServerVersion, rememberPersisted]
  )

  const downloadBackup = useCallback(() => {
    try {
      downloadBlogBackup(latestRef.current, conflictRef.current?.remote)
      return true
    } catch (_error) {
      setStatus({ kind: 'error', message: 'Не вдалося завантажити резервну копію. Версії не змінено.' })
      return false
    }
  }, [])

  const resolveConflict = useCallback(
    async (choice) => {
      const remote = conflictRef.current?.remote
      if (!remote || inFlightRef.current || !downloadBackup()) return
      if (choice === 'local') return saveNow({ replaceRevision: remote.draftRevision })
      const next = { ...remote, _serverSynced: true }
      latestRef.current = next
      markSynced(next)
      callbackRef.current?.(next, true)
      rememberPersisted(await upsertLocalMaterial(next))
    },
    [downloadBackup, saveNow, markSynced, rememberPersisted]
  )

  const discard = useCallback(() => {
    discardedIdRef.current = latestRef.current?.id
    enabledRef.current = false
  }, [])

  const hasUnpersistedChanges =
    enabledRef.current &&
    materialRecord?.id &&
    fingerprint(materialRecord) !== savedFingerprintRef.current &&
    recoveryKey(materialRecord) !== persistedKey
  return {
    status,
    isSaving,
    saveNow,
    markSynced,
    discard,
    conflict,
    inspectServerVersion,
    resolveConflict,
    downloadBackup,
    hasUnpersistedChanges,
  }
}

export default useBlogAutosave
